import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const STORAGE_KEY = 'shifty-pm-theme-mode';

const AppUiContext = createContext(null);

export function AppUiProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v === 'light' ? 'light' : 'dark';
    } catch {
      return 'dark';
    }
  });

  const setMode = useCallback((next) => {
    const m = next === 'light' ? 'light' : 'dark';
    setModeState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const m = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, m);
      } catch {
        /* ignore */
      }
      return m;
    });
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: '#D85A30' },
          secondary: { main: mode === 'dark' ? '#F0997B' : '#B85A2E' },
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
        },
      }),
    [mode],
  );

  const value = useMemo(() => ({ mode, setMode, toggleMode }), [mode, setMode, toggleMode]);

  return (
    <AppUiContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppUiContext.Provider>
  );
}

export function useAppUi() {
  const ctx = useContext(AppUiContext);
  if (!ctx) {
    throw new Error('useAppUi must be used within AppUiProvider');
  }
  return ctx;
}

/** Backdrop tint for loading overlay (works without hooking theme in leaf SVG) */
export function loadingScrimColor(mode) {
  return mode === 'dark' ? alpha('#000000', 0.72) : alpha('#1a1a1a', 0.45);
}
